import { useTranslation } from "@msuite/ui/localization";
import { Panels } from "@msuite/ui/panels";
import { Tabs } from "@msuite/ui/tabs";
import { useIncomingInvoice } from "@/hooks/use-incoming-invoice";
import type { IncomingInvoicePanelBaseProps } from "../../shared";
import { IncomingInvoiceApprovals } from "./incoming-invoice-approvals";
import { IncomingInvoiceChecks } from "./incoming-invoice-checks";
import { IncomingInvoiceDecisions } from "./incoming-invoice-decisions";
import { IncomingInvoiceDuplicateNotice } from "./incoming-invoice-duplicate-notice";
import { IncomingInvoiceIbanNotice } from "./incoming-invoice-iban-notice";

/**
 * The review of an incoming invoice: what is checked on the left; on the right, tabbed, who
 * approves it and what they decided.
 */
export function IncomingInvoiceReview({ incomingInvoiceId }: IncomingInvoicePanelBaseProps) {
	/** Context */
	const { t } = useTranslation();

	/** Query */
	const { incomingInvoice } = useIncomingInvoice(incomingInvoiceId);

	/** Render */
	return (
		<Panels>
			<Panels.Panel
				flex={1}
				className="bottom-space"
			>
				{incomingInvoice && <IncomingInvoiceDuplicateNotice incomingInvoice={incomingInvoice} />}
				{incomingInvoice && <IncomingInvoiceIbanNotice incomingInvoice={incomingInvoice} />}
				<IncomingInvoiceChecks
					checks={incomingInvoice?.checks ?? []}
					currency={incomingInvoice?.currency ?? "EUR"}
				/>
			</Panels.Panel>
			{/* The tabs reach 8px past either side; clipping them keeps the panel from scrolling sideways. */}
			<Panels.Panel
				flex={2}
				className="overflow-x-hidden"
			>
				<Tabs
					panelKey="incoming-invoice-review-tabs"
					groups={[
						{
							name: "approvals",
							panels: [
								{
									label: t("incoming_invoices.approvers_title"),
									cyId: "incoming-invoice-approvers-tab",
									panel: () => <IncomingInvoiceApprovals incomingInvoiceId={incomingInvoiceId} />,
									errors: [],
								},
								{
									label: t("incoming_invoices.decision.history"),
									cyId: "incoming-invoice-decisions-tab",
									panel: () => <IncomingInvoiceDecisions incomingInvoiceId={incomingInvoiceId} />,
									errors: [],
								},
							],
						},
					]}
				/>
			</Panels.Panel>
		</Panels>
	);
}
